import { notificationApi } from '@/api/notifications'
import type { Notification } from '@/types/notification'
import { create } from 'zustand'

interface NotificationState {
  /** Recent in-app notifications pushed via WebSocket (newest first, max 50) */
  recentNotifications: Notification[]
  /** Unread count from backend */
  unreadCount: number

  /** Add a notification from WebSocket push */
  addNotification: (notification: Notification) => void
  /** Set unread count (from API response) */
  setUnreadCount: (count: number) => void
  /** Mark a single notification as read locally */
  markRead: (id: string) => void
  /** Mark all as read locally */
  markAllRead: () => void
  /** Remove a notification locally */
  remove: (id: string) => void
  /** Clear all local notifications */
  clear: () => void
  /** Fetch unread count from API and update store */
  fetchUnreadCount: () => Promise<void>
}

const MAX_RECENT = 50

export const useNotificationStore = create<NotificationState>()((set) => ({
  recentNotifications: [],
  unreadCount: 0,

  addNotification: (notification) =>
    set((state) => ({
      recentNotifications: [notification, ...state.recentNotifications].slice(0, MAX_RECENT),
      unreadCount: state.unreadCount + 1,
    })),

  setUnreadCount: (count) => set({ unreadCount: count }),

  markRead: (id) =>
    set((state) => ({
      recentNotifications: state.recentNotifications.map((n) =>
        n.id === id ? { ...n, readAt: new Date().toISOString(), status: 'read' as const } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),

  markAllRead: () =>
    set((state) => ({
      recentNotifications: state.recentNotifications.map((n) => ({
        ...n,
        readAt: n.readAt ?? new Date().toISOString(),
        status: 'read' as const,
      })),
      unreadCount: 0,
    })),

  remove: (id) =>
    set((state) => ({
      recentNotifications: state.recentNotifications.filter((n) => n.id !== id),
      unreadCount: state.recentNotifications.find((n) => n.id === id && !n.readAt)
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount,
    })),

  clear: () => set({ recentNotifications: [], unreadCount: 0 }),

  fetchUnreadCount: async () => {
    try {
      const resp = await notificationApi.unreadCount()
      set({ unreadCount: resp.count })
    } catch {
      // Silently fail — badge will show stale count
    }
  },
}))
